/**
 * New path data model for improved mobile reliability.
 * Uses point sequences with Bresenham interpolation instead of
 * directional booleans per cell.
 */

/** A single point on the grid */
export type PathPoint = {
  x: number;
  y: number;
};

/** Direction of movement between adjacent cells */
export type Direction = "up" | "down" | "left" | "right";

/** A single path segment for one color */
export type PathSegment = {
  color: string;
  points: PathPoint[];
  isComplete: boolean;
};

/** Complete path state for all colors */
export type PathState = {
  segments: Map<string, PathSegment>; // keyed by color
};

/** Result of attempting to extend a path */
export type ExtendResult = {
  success: boolean;
  newPoints: PathPoint[];
  backtrackIndex?: number; // If set, path should be truncated to this index
  stopDrawing?: boolean; // If true, end the drawing session
  redirectTo?: PathPoint; // For warps/magic boxes that redirect the path
  colorChange?: string; // For painter boxes that change the path color
};

/** Context passed to special tile handlers */
export type TileContext = {
  currentPoint: PathPoint;
  previousPoint: PathPoint | null;
  entryDirection: Direction | null;
  pathSegment: PathSegment;
  allSegments: Map<string, PathSegment>;
  gridSize: number;
  specialTiles: SpecialTileInfo[];
  completedPaths: Set<string>;
};

/** Minimal special tile info for handlers */
export type SpecialTileInfo = {
  x: number;
  y: number;
  type: string;
  color?: string;
};

/** For backwards compatibility with GridBox rendering */
export type GridCellVisual = {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
  color: string;
};

/** Map of cell coordinates to visual data (old format) */
export type GridCellVisualMap = {
  [key: string]: GridCellVisual;
};

/** Get direction from one point to an adjacent point */
export function getDirection(from: PathPoint, to: PathPoint): Direction | null {
  const dx = to.x - from.x;
  const dy = to.y - from.y;

  if (dx === 1 && dy === 0) return "right";
  if (dx === -1 && dy === 0) return "left";
  if (dx === 0 && dy === 1) return "down";
  if (dx === 0 && dy === -1) return "up";

  return null; // Not adjacent or same point
}

/** Get the opposite direction */
export function oppositeDirection(dir: Direction): Direction {
  switch (dir) {
    case "up":
      return "down";
    case "down":
      return "up";
    case "left":
      return "right";
    case "right":
      return "left";
  }
}

/** Check if two points are equal */
export function pointsEqual(a: PathPoint, b: PathPoint): boolean {
  return a.x === b.x && a.y === b.y;
}

/** Get string key for a point (for Map/Set usage) */
export function pointKey(p: PathPoint): string {
  return `${p.x},${p.y}`;
}

/** Parse a point key back to a PathPoint */
export function parsePointKey(key: string): PathPoint {
  const [x, y] = key.split(",").map(Number);
  return { x, y };
}
